const DISPLAY_SIZE = {
  width: 650,
  height: 365.625
};

export function autoDetectCategory(zone) {
  if (!zone?.availableForAdvertising) {
    return {
      category: null,
      confidence: 1.0,
      reasoning: "Internal use only - no category needed",
      autoDetected: false
    };
  }

  const aspectRatio = (zone?.width || 0) / (zone?.height || 1);
  const zoneArea = (zone?.width || 0) * (zone?.height || 0);
  const displayArea = DISPLAY_SIZE.width * DISPLAY_SIZE.height;
  const percentOfDisplay = (zoneArea / displayArea) * 100;

  if ((zone?.height || 0) < 150 && aspectRatio > 5) {
    return {
      category: 'ticker',
      confidence: 0.98,
      reasoning: "Height <150px with very wide aspect ratio",
      autoDetected: true
    };
  }

  if (percentOfDisplay > 90) {
    return {
      category: 'fullscreen',
      confidence: 0.95,
      reasoning: "Takes up >90% of display area",
      autoDetected: true
    };
  }

  const isPrimary = percentOfDisplay > 50;
  const isSecondary = percentOfDisplay > 15 && percentOfDisplay <= 50;

  const isLandscape = aspectRatio > 1.3;
  const isPortrait = aspectRatio < 0.77;
  const isSquare = aspectRatio >= 0.77 && aspectRatio <= 1.3;
  if (isPrimary && isLandscape) {
    return {
      category: 'primary-horizontal',
      confidence: 0.95,
      reasoning: `Primary zone (>50% of display) with landscape orientation (${aspectRatio.toFixed(2)}:1)`,
      autoDetected: true
    };
  }

  if (isPrimary && isPortrait) {
    return {
      category: 'primary-vertical',
      confidence: 0.95,
      reasoning: `Primary zone (>50% of display) with portrait orientation (${aspectRatio.toFixed(2)}:1)`,
      autoDetected: true
    };
  }

  if (isPrimary && isSquare) {
    return {
      category: 'primary-square',
      confidence: 0.95,
      reasoning: `Primary zone (>50% of display) with square orientation (${aspectRatio.toFixed(2)}:1)`,
      autoDetected: true
    };
  }

  if (isSecondary && isLandscape) {
    return {
      category: 'secondary-horizontal',
      confidence: 0.92,
      reasoning: `Secondary zone (15-50% of display) with landscape orientation (${aspectRatio.toFixed(2)}:1)`,
      autoDetected: true
    };
  }

  if (isSecondary && isPortrait) {
    return {
      category: 'secondary-vertical',
      confidence: 0.92,
      reasoning: `Secondary zone (15-50% of display) with portrait orientation (${aspectRatio.toFixed(2)}:1)`,
      autoDetected: true
    };
  }

  if (isSecondary && isSquare) {
    return {
      category: 'secondary-square',
      confidence: 0.92,
      reasoning: `Secondary zone (15-50% of display) with square orientation (${aspectRatio.toFixed(2)}:1)`,
      autoDetected: true
    };
  }

  return {
    category: 'custom',
    confidence: 0.7,
    reasoning: "Doesn't fit standard patterns - custom category",
    autoDetected: true
  };
}

export function getCategoryDisplayName(category) {
  const categoryNames = {
    'primary-horizontal': 'Primary Horizontal',
    'primary-vertical': 'Primary Vertical',
    'primary-square': 'Primary Square',
    'secondary-horizontal': 'Secondary Horizontal',
    'secondary-vertical': 'Secondary Vertical',
    'secondary-square': 'Secondary Square',
    'ticker': 'Ticker',
    'fullscreen': 'Full Screen',
    'led': 'LED',
    'custom': 'Custom',
    null: 'Not needed (internal use only)'
  };
  return categoryNames[category] || 'Custom';
}

export function getAspectRatioString(width, height) {
  const w = width || 0;
  const h = height || 1;
  const ratio = h === 0 ? 0 : w / h;
  
  if (Math.abs(ratio - 16/9) < 0.1) return '16:9';
  if (Math.abs(ratio - 9/16) < 0.1) return '9:16';
  if (Math.abs(ratio - 1) < 0.1) return '1:1';
  if (Math.abs(ratio - 4/3) < 0.1) return '4:3';
  if (Math.abs(ratio - 3/4) < 0.1) return '3:4';
  if (Math.abs(ratio - 21/9) < 0.1) return '21:9';
  
  const roundRatio = (num) => parseFloat(num.toFixed(2));
  return `${roundRatio(ratio)}:1`;
}

export function getCategoryOptions() {
  return [
    { value: 'primary-horizontal', label: 'Primary Horizontal' },
    { value: 'primary-vertical', label: 'Primary Vertical' },
    { value: 'primary-square', label: 'Primary Square' },
    { value: 'secondary-horizontal', label: 'Secondary Horizontal' },
    { value: 'secondary-vertical', label: 'Secondary Vertical' },
    { value: 'secondary-square', label: 'Secondary Square' },
    { value: 'ticker', label: 'Ticker' },
    { value: 'fullscreen', label: 'Full Screen' },
    { value: 'led', label: 'LED' },
    { value: 'custom', label: 'Custom' }
  ];
}

